// Helping workspace members find their revenue-split payouts (owner, 23 Oct 2026; photohawk-serverless
// docs/revenue-splitting-design.md §0000c): an "Earnings" item in the account menus — the email dropdown and the
// workspace avatar menu — (a dot while payouts aren't set up) and a "You have £42.10 waiting — set up payouts" banner on Your workspaces and the uploader.
// Everything reads GET /admin/me/earnings through the one shared query (useMyEarnings), so pages don't add
// requests of their own.
import { useCognitoAuth } from "@/hooks/useCognitoAuth";
import { useMyEarnings, type Earnings } from "@/lib/api/revenueSplit";
import { money } from "@/lib/format";

/** Account → Earnings. */
export const EARNINGS_PATH = "/account?tab=earnings";

/** sessionStorage key: the banner was dismissed for this session (cleared on a user change, lib/perUserCaches). */
export const BANNER_DISMISSED_KEY = "ph_earnings_banner_dismissed";

// The menu and banner don't need fresher numbers than this; the Earnings tab still refreshes as it does today.
const SIGNPOST_STALE_MS = 10 * 60_000;

/** Earnings shows (account menu + Account tab) for anyone with earnings, who started payout setup, or is in a split. */
export const showsEarnings = (e: Earnings | null | undefined): e is Earnings =>
  !!e && (e.hasEarnings || e.payoutStatus !== "not_onboarded" || !!e.inSplit);

/** Payout setup isn't finished (the menu's dot). */
export const payoutSetupUnfinished = (e: Earnings | null | undefined): boolean => showsEarnings(e) && e.payoutStatus !== "payable";

/**
 * The money waiting for payout setup, formatted in its own currency: "£42.10", "£42.10 and €15.00", or with
 * three or more currencies the largest one and "and more" (the server orders them by value, converted). Null when nothing is waiting or setup is done.
 */
export const waitingAmount = (e: Earnings | null | undefined): string | null => {
  if (!e || e.payoutStatus === "payable") return null;
  const list = (e.waitingForSetup || []).filter((w) => w && w.amount > 0);
  if (!list.length) return null;
  if (list.length === 1) return money(list[0].amount, list[0].currency);
  if (list.length === 2) return `${money(list[0].amount, list[0].currency)} and ${money(list[1].amount, list[1].currency)}`;
  return `${money(list[0].amount, list[0].currency)} and more`;
};

/** The signed-in user's earnings for the menu / banner (shared cache; nothing when signed out). */
export const useEarningsSignpost = () => {
  const { isAuthenticated } = useCognitoAuth();
  const { data } = useMyEarnings(isAuthenticated, SIGNPOST_STALE_MS);
  return isAuthenticated ? data ?? null : null;
};

export const readBannerDismissed = (): boolean => {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

export const writeBannerDismissed = (): void => {
  try {
    window.sessionStorage.setItem(BANNER_DISMISSED_KEY, "1");
  } catch {
    /* storage unavailable (private mode) — dismissed until the page reloads */
  }
};
